import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EyeSlashIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from '@remix-run/react';
import type { JSONContent } from '@tiptap/core';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import { useCommunityMembership } from '~/routes/community/_hooks/useCommunityMembership';
import { getActionErrorMessage } from '~/routes/community/_utils/getActionErrorMessage';
import { isCommunityComment } from '~/routes/community/_utils/isCommunityComment';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';
import toast from '~/utils/toast';

import { CommentComposer } from './components/CommentComposer';
import { CommentThread } from './components/CommentThread';

import { type CommentThreadPreview, useCommentThread } from './_hooks/useCommentThread';

interface CommentSectionProps {
  postId: string;
  // The post's space, which limits who can be mentioned. The website post page passes none.
  spaceSlug?: string;
  commentsState: 'open' | 'closed' | 'hidden';
  preview?: CommentThreadPreview;
  showFullThread?: boolean;
  onShowFullThread?: () => void;
  onRequireMembership?: () => void;
  onCommentCreated?: () => void;
  onCommentDeleted?: () => void;
  onDeleteRollback?: () => void;
}

export const CommentSection: FC<CommentSectionProps> = ({
  postId,
  spaceSlug,
  commentsState,
  preview,
  showFullThread,
  onShowFullThread,
  onRequireMembership,
  onCommentCreated,
  onCommentDeleted,
  onDeleteRollback,
}) => {
  const { t } = useTranslation();
  const { permissions } = useCommunityMembership();
  const fetcher = useNonRevalidatingFetcher();
  const location = useLocation();
  const navigate = useNavigate();
  const hashCommentId = useLastHashCommentId(postId, location.hash);
  // Feed cards render a preview and share the page hash, so only a section without a preview follows it.
  const targetCommentId = preview ? undefined : hashCommentId;
  const thread = useCommentThread(postId, {
    commentId: targetCommentId,
    preview,
    showFullThread,
    onCommentDeleted,
    onDeleteRollback,
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const isSubmitting = fetcher.state === 'submitting';
  const isClosed = commentsState === 'closed';
  const isHidden = commentsState === 'hidden';
  const isHiddenFromViewer = isHidden && !permissions.canHideAnyComments;
  const canComposeAfterJoining = Boolean(onRequireMembership) && !permissions.isMember;
  const showComposer = (permissions.canComment || canComposeAfterJoining) && commentsState === 'open';

  const handleSubmit = useCallback(
    async (json: JSONContent) => {
      if (!permissions.canComment) {
        onRequireMembership?.();
        return;
      }

      const formData = new FormData();
      formData.append('post_id', postId);
      formData.append('content_json', JSON.stringify(json));
      withNectarTrackingParams(formData, NectarPageBlock.CommunityPostCommentForm);

      const response = await fetcher.submit(formData, { method: 'POST', action: '/community_comment' });
      if (!response.ok) {
        toast.error(getActionErrorMessage(response.data) ?? t('community.comments.submit_error'));
        return;
      }
      if (!isCommunityComment(response.data)) {
        console.error('Unexpected comment response shape', response.data);
        return;
      }

      if (thread.focusedCommentId) {
        navigate({ ...location, hash: `#${response.data.id}` }, { replace: true, state: location.state });
      } else {
        thread.addComment(response.data);
        setIsExpanded(true);
      }
      onCommentCreated?.();
    },
    [
      postId,
      fetcher,
      onCommentCreated,
      thread.addComment,
      thread.focusedCommentId,
      navigate,
      location,
      t,
      permissions.canComment,
      onRequireMembership,
    ],
  );

  return (
    <section>
      {(isClosed || isHidden) && (
        <div
          role="status"
          aria-live="polite"
          className="mx-2 my-3 rounded-2xl border border-black/10 bg-black/5 px-4 py-3 text-sm text-black/70"
        >
          {t(isClosed ? 'community.post.comments_closed_banner' : 'community.post.comments_hidden_banner')}
        </div>
      )}
      {isHidden && permissions.canHideAnyComments && (
        <div className="mx-4 -mt-1 mb-3 flex items-center gap-1.5 text-xs text-black/60">
          <EyeSlashIcon className="size-3.5" />
          <span>{t('community.post.hidden_comments_mod_notice')}</span>
        </div>
      )}
      {!isHiddenFromViewer && (
        <>
          <CommentThread
            postId={postId}
            thread={thread}
            isExpanded={isExpanded || Boolean(preview && showFullThread)}
            commentsState={commentsState}
            onExpand={() => setIsExpanded(true)}
            onShowFullThread={onShowFullThread}
            onCommentCreated={onCommentCreated}
            spaceSlug={spaceSlug}
          />
          {showComposer && !thread.isPreviewing && (
            <CommentComposer onSubmit={handleSubmit} isSubmitting={isSubmitting} spaceSlug={spaceSlug} />
          )}
        </>
      )}
    </section>
  );
};

// Returns the comment id from the url hash, or the last one seen on this post once the hash is gone.
// Login links carry a jwt_token that the app strips from the url with a navigation that also drops
// the hash, which would otherwise unfocus the linked comment right after it renders.
const useLastHashCommentId = (postId: string, hash: string) => {
  const hashCommentId = hash.replace(/^#/, '') || undefined;
  const [savedTarget, setSavedTarget] = useState({ postId, commentId: hashCommentId });

  useEffect(() => {
    if (hashCommentId) setSavedTarget({ postId, commentId: hashCommentId });
  }, [postId, hashCommentId]);

  return hashCommentId ?? (savedTarget.postId === postId ? savedTarget.commentId : undefined);
};
